<template>
  <div>
    <BreadcrumbMapObject v-if="mapObject"
                         :mapObject="mapObject"
                         :extraLabel="$t('button.move')"
                         @clickBack="clickBack"
                         :show-parents="false"
    />
    <CollapsePanel
      :collapsed="true"
      :loading=loading
      :showCount=false
      :title="$t('drawing.information')"
    >
      <div v-if="mapObject && selectedType && selectedType.geo_type"
           class="information-block"
      >
        <p>
          {{ $t(`drawing.move_${selectedType.geo_type}`) }}
        </p>
        <div id="change-geo-directly" v-if="isPoint">
          <label for="input-field-object-lat">
            Latitude
          </label>
          <div id="input-field-object-lat" class="field">
            <input id="object-lat" type="number" name="object-lat" v-model.number="newCoordinates[1]"
                   style="padding: 0 0 0 7px;" placeholder=" " step="0.001" @input="updateCoordsThroughInput">
          </div>
          <label for="input-field-object-lng">
            Longitude
          </label>
          <div id="input-field-object-lng" class="field">
            <input id="object-lng" type="number" name="object-lng" v-model.number="newCoordinates[0]"
                   style="padding: 0 0 0 7px;" placeholder=" " step="0.001" @input="updateCoordsThroughInput">
          </div>
        </div>
        <div id="move-buttons" v-else-if="!movePoint">
          <a v-if="startPoint"
             class="btn"
             @click="changeMovePoint('start')"
          >
            {{ $t('drawing.move_startpoint') }}
            (<span id="move-startpoint-label">{{ startPoint | mapObjectLabel }}</span>)
          </a>
          <br/><br/>
          <a v-if="endPoint"
             class="btn"
             @click="changeMovePoint('end')"
          >
            {{ $t('drawing.move_endpoint') }}
            (<span id="move-endpoint-label">{{ endPoint | mapObjectLabel }}</span>)
          </a>
        </div>
        <div v-else id="move-point-description">
          <div v-if="movePoint === 'start'">
            {{ $t('drawing.choose_new_startpoint') }}
            <br/>
          </div>
          <div v-else-if="movePoint === 'end'">
            {{ $t('drawing.choose_new_endpoint') }}
            <br/>
          </div>
          <div>
            <a class="btn red"
               @click="cancelChangeMovePoint()"
            >
              {{ $t('drawing.cancel_move') }}
            </a>
          </div>
        </div>
      </div>
    </CollapsePanel>
    <ButtonRow @back="clickBack" @save="saveObject"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import BreadcrumbMapObject from '@/components/Layout/BreadcrumbMapObject.vue';
import CollapsePanel from '@/components/CollapsePanel.vue';
import MapObjectApi from '@/api/map_object';
import EventBus from '@/events/event-bus';
import MapMode from '@/enums/MapMode';
import ButtonRow from '@/components/ButtonRow.vue';
import GeoType from '@/enums/GeoType';
import * as dataCache from '@/store/cache/dataCache';

export default {
  name: 'drawing.move_object',
  components: {
    ButtonRow,
    BreadcrumbMapObject,
    CollapsePanel,
  },
  computed: {
    ...mapGetters({
      mapObject: 'selected/getMapObject',
      mapObjectTypes: 'settings/getMapObjectTypes',
      network: 'settings/getNetworkModel',
    }),
    isPoint() {
      return this.geoType === GeoType.point;
    },
    selectedType() {
      if (!this.mapObjectTypes) {
        return null;
      }
      if (!this.mapObject) {
        return null;
      }
      return this.mapObjectTypes[this.mapObject.map_object_type_id];
    },
  },
  data() {
    return {
      geoType: null,
      loading: false,
      newCoordinates: [],
      startPoint: null,
      endPoint: null,
      movePoint: false,
    };
  },
  methods: {
    ...mapActions({
      fetchMapObject: 'selected/fetchMapObject',
      getChildrenForMapObject: 'settings/getChildrenForMapObject',
      getLinestringsByStartOrEndPoint: 'settings/getLinestringsByStartOrEndPoint',
    }),
    clickBack() {
      EventBus.$emit('Map.changeMapMode', MapMode.objectSelect);
      EventBus.$emit('Map.redrawMapObject', this.mapObject);
      if (this.geoType === GeoType.point) {
        EventBus.$emit('Map.cancelConnectedLines');
      }
      this.$router.back();
    },
    async saveObject() {
      if (this.geoType === GeoType.linestring) {
        if (!this.startPoint || !this.startPoint.id) {
          this.$notify({
            type: 'warning',
            title: this.$t('error.update_failed', { model: this.$tc('models.mapobject', 1) }),
            text: this.$t('validation.no_start_point'),
            duration: 2000,
          });
          return null;
        }
        if (!this.endPoint || !this.endPoint.id) {
          this.$notify({
            type: 'warning',
            title: this.$t('error.update_failed', { model: this.$tc('models.mapobject', 1) }),
            text: this.$t('validation.no_end_point'),
            duration: 2000,
          });
          return null;
        }
      }
      this.loading = true;

      const data = {
        data: JSON.parse(JSON.stringify(this.mapObject)),
      };
      data.data.geoObject.data.geometry.coordinates = this.newCoordinates;
      if (this.geoType === GeoType.linestring) {
        data.data.geoObject.data.properties.start_point = this.startPoint.id;
        data.data.geoObject.data.properties.end_point = this.endPoint.id;
      }
      delete data.data.children;

      const children = await this.getChildrenForMapObject({ id: this.mapObject.id });
      const ids = children.filter((item) => {
        const type = this.mapObjectTypes[item.map_object_type_id];
        return type ? !type.mapObjectTypeSetting.data.find(setting => setting.name === 'hasOwnGeo') : false;
      }).map(item => item.id);

      return MapObjectApi.updateMapObjectById(this.mapObject.id, data)
        .then((response) => {
          if (ids.length > 0) {
            const updateData = {
              update: {
                geoObject: response.data.geoObject,
              },
              ids,
            };
            MapObjectApi.updateMapObjectsInBulk(updateData).then(() => {
              this.updateLocalChildren(updateData);
            });
          }
          this.fetchMapObject({ id: this.mapObject.id, force: true }).then((newMapObject) => {
            EventBus.$emit('Map.redrawMapObject', newMapObject);
          }).finally(() => {
            this.$notify({
              type: 'success',
              title: this.$t('dialog.created', {
                model: this.$options.filters.capitalize(this.$tc('models.mapobject', 1)),
                name: this.$options.filters.mapObjectLabel(this.mapObject),
              }),
            });
            if (this.geoType === GeoType.point) {
              EventBus.$emit('Map.cancelConnectedLines', false);
              EventBus.$emit('Map.updateRelatedLines', this.mapObject.id, this.newCoordinates);
            }
            setTimeout(() => {
              this.loading = false;
              EventBus.$emit('Map.changeMapMode', MapMode.objectSelect);
              return this.$router.push({ name: 'drawing.show', params: { id: this.mapObject.id } });
            }, 500);
          });
        })
        .catch((error) => {
          console.log(error);
          this.$notify({
            type: 'error',
            title: this.$t('error.create_failed', { model: this.$tc('models.mapobject', 1) }),
            data: error.response,
          });
          this.loading = false;
          return false;
        });
    },
    async updateLocalChildren(data) {
      await Promise.all(data.ids.map(async (id) => {
        const mapObject = await this.getMapObjectById({ mapObjectId: id });
        if (!mapObject) return;

        if (data.update.geo_object_id) {
          mapObject.geo_object_id = data.update.geo_object_id;
        }
        if (data.update.geoObject) {
          mapObject.geoObject = data.update.geoObject;
        }

        if (data.update.attribute && data.update.attribute.data) {
          const labelAttributes = data.update.attribute.data
            .filter(item => item.id === this.chosenMapObjectType.label_attribute_id);
          if (labelAttributes.length > 0) {
            mapObject.label = labelAttributes[0].value || mapObject.label;
          }
        }

        if (data.update.map_object_status_id) {
          mapObject.map_object_status_id = data.update.map_object_status_id;
          delete mapObject.mapObjectStatus;
        }

        await this.updateMapObject({ mapObject, redraw: false });
      }));
    },
    changeMovePoint(point) {
      this.movePoint = point;
      EventBus.$emit('Map.findNewPoint', point);
    },
    cancelChangeMovePoint() {
      this.movePoint = false;
      EventBus.$emit('Map.stopFindNewPoint');
    },
    async setupMapObject() {
      this.loading = true;
      if (this.mapObject.geoObject.data.properties.start_point) {
        this.startPoint = dataCache.getMapObjectById(this.mapObject.geoObject.data.properties.start_point);
      }
      if (this.mapObject.geoObject.data.properties.end_point) {
        this.endPoint = dataCache.getMapObjectById(this.mapObject.geoObject.data.properties.end_point);
      }
      this.changeMapMode(this.mapObject);
      this.loading = false;
      if (this.geoType === GeoType.point) {
        EventBus.$emit('Map.removeMapObjectFromMap', this.mapObject.id);
        const connectedLines = await this.getLinestringsByStartOrEndPoint({ id: this.mapObject.id });
        EventBus.$emit('Map.setConnectedLines', connectedLines);
      }
    },
    changeMapMode(mapObject) {
      const data = JSON.parse(JSON.stringify(mapObject));
      const type = this.mapObjectTypes[data.map_object_type_id];
      if (type) {
        this.geoType = type.geo_type;
        EventBus.$emit(
          'Map.changeMapMode',
          MapMode.objectUpdate,
          this.geoType,
          data,
        );
      }
    },
    updateCoordsThroughInput() {
      EventBus.$emit('Map.updateCoordinates', this.newCoordinates);
    },
  },
  mounted() {
    if (this.$route.params.id !== null && this.$route.params.id !== undefined) {
      const id = parseInt(this.$route.params.id, 10);
      if (!this.mapObject || this.mapObject.id !== id) {
        this.fetchMapObject({ id });
      } else {
        this.setupMapObject();
      }
    }
    EventBus.$off('Map.shareCoordinates');
    EventBus.$on('Map.shareCoordinates', (coordinates) => {
      console.log('EventBus.$on(Map.shareCoordinates)', this.mapObject);
      this.newCoordinates = coordinates;
      if (this.geoType === GeoType.point) {
        EventBus.$emit('Map.updateConnectedLines', this.mapObject.id, coordinates);
      }
    });

    EventBus.$off('Map.newPoint');
    EventBus.$on('Map.newPoint', (mapObject) => {
      console.log('EventBus.$on(Map.newPoint)');
      if (this.movePoint === 'start') {
        this.startPoint = mapObject;
      } else if (this.movePoint === 'end') {
        this.endPoint = mapObject;
      }
      this.movePoint = false;
    });
  },
  watch: {
    mapObject() {
      this.setupMapObject();
    },
  },
};
</script>

<style scoped>
.information-block {
  padding: 0 20px;
}

#move-buttons {
  margin-bottom: 10px;
  display: inline-block;
}

#move-point-description {
  margin-top: 5px;
  font-style: italic;
  margin-bottom: 10px;
  display: inline-block;
}

#move-point-description a.btn {
  margin-top: 5px;
}
</style>
